import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatDt = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN');
  } catch {
    return String(v);
  }
};

export default function OnlineCourseSales() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [publisher, setPublisher] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [courseCode, setCourseCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/local-learn/admin/online-sales', {
        params: {
          q: q.trim() || undefined,
          publisher,
          from: from || undefined,
          to: to || undefined,
          courseCode: courseCode.trim() || undefined,
          limit: 200,
        },
      });
      const data = res.data?.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setStats(data.stats || null);
      setTotal(Number(data.total) || 0);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
      setRows([]);
      setStats(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, publisher, from, to, courseCode]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const statCards = useMemo(
    () => [
      {
        label: 'Our sales (KNLS)',
        value: money(stats?.ourSalesAmount),
        sub: `${stats?.ourSalesCount ?? 0} paid`,
        color: '#0f766e',
      },
      {
        label: 'Today (KNLS)',
        value: money(stats?.ourSalesTodayAmount),
        sub: `${stats?.ourSalesTodayCount ?? 0} paid`,
        color: '#0369a1',
      },
      {
        label: 'This month (KNLS)',
        value: money(stats?.ourSalesMonthAmount),
        sub: `${stats?.ourSalesMonthCount ?? 0} paid`,
        color: '#7c3aed',
      },
      {
        label: 'Mitra / third-party',
        value: money(stats?.mitraAmount),
        sub: `${stats?.mitraCount ?? 0} — not in our sales (settle later)`,
        color: '#b45309',
      },
    ],
    [stats],
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <PointOfSaleIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>
          Online Course Sales
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 780 }}>
        Citizen Learn Zone and app course purchases. <strong>Our sales total</strong> includes
        KNLS / platform only. Mitra / third-party payments are listed for settlement later and
        are not counted in our sales.
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {statCards.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card variant="outlined" sx={{ height: '100%', borderLeft: `4px solid ${c.color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: c.color }}>
                  {loading && !stats ? '…' : c.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.sub}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Name, mobile, course, payment id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Publisher"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="platform">KNLS / our sales</MenuItem>
              <MenuItem value="mitra">Mitra / third-party</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Course code"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography color="text.secondary">No online sales found</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paid at</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Mobile (login)</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Publisher</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Our sales?</TableCell>
                <TableCell>Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDt(r.paidAt)}</TableCell>
                  <TableCell>{r.studentName || '—'}</TableCell>
                  <TableCell>{r.studentPhone || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {r.courseName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.courseCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.publisherSource === 'mitra' ? 'Mitra' : 'KNLS'}
                      color={r.publisherSource === 'mitra' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{money(r.amountRupees)}</TableCell>
                  <TableCell>
                    {r.countsInOurSales ? (
                      <Chip size="small" label="Yes" color="success" />
                    ) : (
                      <Chip size="small" label="Settle later" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 140 }}>
                    <Typography variant="caption" noWrap display="block">
                      {r.paymentId || r.orderId || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Showing {rows.length} of {total} matching records
      </Typography>
    </Box>
  );
}
